import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { academicApi } from '../features/academics/services/academicApi';
import { schoolSettingsApi } from '../features/settings/services/schoolSettingsApi';

const SchoolContext = createContext(null);

const readCachedJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error(`Failed to read ${key} from localStorage`, error);
    return null;
  }
};

const getDefaultSchoolId = (user) =>
  user?.school_id || user?.schoolId || localStorage.getItem('school_id') || '';

const sortAcademicYears = (years = []) =>
  [...years].sort((left, right) => {
    const leftStart = String(left?.start_date || '');
    const rightStart = String(right?.start_date || '');
    return rightStart.localeCompare(leftStart);
  });

export const SchoolProvider = ({ children }) => {
  const { user, isSuperuser } = useAuth();
  const accountSchoolId = getDefaultSchoolId(user);

  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(accountSchoolId);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const [schoolProfile, setSchoolProfileState] = useState(() =>
    readCachedJson('school_profile'),
  );
  const [branding, setBrandingState] = useState(() =>
    readCachedJson('school_branding'),
  );
  const [profileLoading, setProfileLoading] = useState(false);
  const [brandingLoading, setBrandingLoading] = useState(false);

  // Working academic year is deliberately in memory only. It resets on logout/login.
  const [academicYears, setAcademicYears] = useState([]);
  const [activeAcademicYearId, setActiveAcademicYearIdState] = useState('');
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const [academicYearError, setAcademicYearError] = useState('');

  const setSchoolProfile = useCallback((profile) => {
    setSchoolProfileState(profile || null);
    if (profile) {
      localStorage.setItem('school_profile', JSON.stringify(profile));
    } else {
      localStorage.removeItem('school_profile');
    }
  }, []);

  const setBranding = useCallback((value) => {
    setBrandingState(value || null);
    if (value) {
      localStorage.setItem('school_branding', JSON.stringify(value));
    } else {
      localStorage.removeItem('school_branding');
    }
  }, []);

  const refreshSchools = useCallback(async () => {
    if (!user || !isSuperuser) {
      setSchools([]);
      return [];
    }

    setSchoolsLoading(true);
    try {
      const rows = await schoolSettingsApi.listSchools();
      setSchools(rows || []);
      return rows || [];
    } finally {
      setSchoolsLoading(false);
    }
  }, [isSuperuser, user]);

  const refreshProfile = useCallback(async () => {
    if (!schoolId) return null;
    setProfileLoading(true);
    try {
      const profile = await schoolSettingsApi.getProfile(schoolId);
      setSchoolProfile(profile);
      return profile;
    } finally {
      setProfileLoading(false);
    }
  }, [schoolId, setSchoolProfile]);

  const refreshBranding = useCallback(async () => {
    if (!schoolId) return null;
    setBrandingLoading(true);
    try {
      const value = await schoolSettingsApi.getBranding(schoolId);
      setBranding(value);
      return value;
    } finally {
      setBrandingLoading(false);
    }
  }, [schoolId, setBranding]);

  const refreshAcademicYears = useCallback(
    async ({ preserveSelection = true } = {}) => {
      if (!schoolId) {
        setAcademicYears([]);
        setActiveAcademicYearIdState('');
        return [];
      }

      setAcademicYearLoading(true);
      setAcademicYearError('');
      try {
        const rows = sortAcademicYears(
          await academicApi.getAcademicYears(schoolId, { status: 'ACTIVE' }),
        );
        setAcademicYears(rows);

        setActiveAcademicYearIdState((currentId) => {
          if (
            preserveSelection &&
            currentId &&
            rows.some((item) => String(item.id) === String(currentId))
          ) {
            return currentId;
          }

          const backendCurrent = rows.find((item) => Boolean(item.is_current));
          return String(backendCurrent?.id || rows[0]?.id || '');
        });
        return rows;
      } catch (error) {
        setAcademicYears([]);
        setActiveAcademicYearIdState('');
        setAcademicYearError(
          error?.response?.data?.message ||
            error?.message ||
            'Unable to load academic years.',
        );
        throw error;
      } finally {
        setAcademicYearLoading(false);
      }
    },
    [schoolId],
  );

  const selectSchool = useCallback(
    (nextSchoolId) => {
      const normalized = String(nextSchoolId || '');
      if (!isSuperuser) return;
      if (normalized === String(schoolId || '')) return;

      setSchoolId(normalized);
      setSchoolProfileState(null);
      setBrandingState(null);
      setAcademicYears([]);
      setActiveAcademicYearIdState('');
      setAcademicYearError('');
      localStorage.removeItem('school_profile');
      localStorage.removeItem('school_branding');
    },
    [isSuperuser, schoolId],
  );

  const setActiveAcademicYear = useCallback(
    (academicYearOrId) => {
      const nextId = String(
        typeof academicYearOrId === 'object'
          ? academicYearOrId?.id || ''
          : academicYearOrId || '',
      );
      if (!nextId) return;
      if (!academicYears.some((item) => String(item.id) === nextId)) return;
      setActiveAcademicYearIdState(nextId);
    },
    [academicYears],
  );

  useEffect(() => {
    if (!user) {
      setSchoolId('');
      setSchools([]);
      setSchoolProfileState(null);
      setBrandingState(null);
      setAcademicYears([]);
      setActiveAcademicYearIdState('');
      setAcademicYearError('');
      localStorage.removeItem('school_profile');
      localStorage.removeItem('school_branding');
      return;
    }

    if (!isSuperuser) {
      setSchoolId(accountSchoolId);
      setSchools([]);
      return;
    }

    let cancelled = false;
    refreshSchools()
      .then((rows) => {
        if (cancelled) return;
        const preferred =
          rows.find((item) => String(item.id) === String(accountSchoolId)) || rows[0];
        setSchoolId((current) => current || String(preferred?.id || ''));
      })
      .catch((error) => {
        console.error('Failed to load schools for super admin', error);
        if (!cancelled) setSchoolId(accountSchoolId);
      });

    return () => {
      cancelled = true;
    };
  }, [accountSchoolId, isSuperuser, refreshSchools, user]);

  useEffect(() => {
    if (!user || !schoolId) {
      return;
    }

    refreshProfile().catch((error) => {
      console.error('Failed to load school profile', error);
    });
    refreshBranding().catch((error) => {
      console.error('Failed to load school branding', error);
    });
    refreshAcademicYears({ preserveSelection: false }).catch((error) => {
      console.error('Failed to load academic years', error);
    });
  }, [
    user,
    schoolId,
    refreshProfile,
    refreshBranding,
    refreshAcademicYears,
  ]);

  const activeAcademicYear = useMemo(
    () =>
      academicYears.find(
        (item) => String(item.id) === String(activeAcademicYearId),
      ) || null,
    [academicYears, activeAcademicYearId],
  );

  const value = useMemo(
    () => ({
      schoolId,
      accountSchoolId,
      schools,
      schoolsLoading,
      selectSchool,
      refreshSchools,
      schoolProfile,
      branding,
      profileLoading,
      brandingLoading,
      setSchoolProfile,
      setBranding,
      refreshProfile,
      refreshBranding,
      academicYears,
      activeAcademicYear,
      activeAcademicYearId,
      academicYearLoading,
      academicYearError,
      setActiveAcademicYear,
      refreshAcademicYears,
    }),
    [
      schoolId,
      accountSchoolId,
      schools,
      schoolsLoading,
      selectSchool,
      refreshSchools,
      schoolProfile,
      branding,
      profileLoading,
      brandingLoading,
      setSchoolProfile,
      setBranding,
      refreshProfile,
      refreshBranding,
      academicYears,
      activeAcademicYear,
      activeAcademicYearId,
      academicYearLoading,
      academicYearError,
      setActiveAcademicYear,
      refreshAcademicYears,
    ],
  );

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
};

export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error('useSchool must be used inside SchoolProvider.');
  }
  return context;
};
